import { Alert, Box, Divider, Stack, Typography } from "@mui/material";
import {
  type BulkManifestDiscrepanciesQuery,
  WasteManifestSource,
} from "generated-graphql/graphql";

import { verdictText } from "./DataSourceConflictsPane.helpers";
import { DetailGrid, DetailRow } from "./discrepancyDetailBlocks";
import {
  type DiscrepancyStatus,
  hasResolvableAction,
  formatDateOnly,
  formatDisplayValue,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyRowExpanderShell } from "./DiscrepancyRowExpanderShell";
import { SOURCE_LABEL } from "./manifestSourceDisplay";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/**
 * One "does this source differ" verdict. Label and answer stay in one text node
 * so the line reads as a sentence, unlike the value pairs above it, which are a
 * column of like things on a grid.
 */
function VerdictLine({ label, verdict }: { label: string; verdict: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{" "}
      {verdict}
    </Typography>
  );
}

export function DataSourceConflictRowExpander({
  discrepancy,
  status,
  open,
  colSpan,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
}: {
  discrepancy: Discrepancy;
  status: DiscrepancyStatus;
  open: boolean;
  colSpan: number;
  /** Every row action here is staff-only; the row still renders values, verdicts, and history for a customer. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  mutationLoading?: boolean;
}) {
  const format = discrepancy.field === "shippedDate" ? formatDateOnly : formatDisplayValue;

  return (
    <DiscrepancyRowExpanderShell
      discrepancy={discrepancy}
      status={status}
      open={open}
      colSpan={colSpan}
      isStaff={isStaff}
      canResolve={hasResolvableAction(discrepancy, true)}
      isMutating={mutationLoading === true}
      onResolve={() => onResolve(discrepancy)}
      onMarkUnresolved={() => onMarkUnresolved(discrepancy)}
      onUndoResolution={() => onUndoResolution(discrepancy)}
    >
      {/* fit-content so the divider spans the values it separates from the
          verdicts, rather than the whole table. */}
      <Box sx={{ width: "fit-content", maxWidth: "100%" }}>
        {/* Every source shows, a dash where it holds no value — which sources
            hold one is the point of a comparison row. */}
        <DetailGrid>
          <DetailRow label="e-Manifest" value={format(discrepancy.eManifestValue)} />
          <DetailRow label="Vendor" value={format(discrepancy.vendorValue)} />
          <DetailRow
            label={SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
            value={format(discrepancy.manifestDocumentValue)}
          />
        </DetailGrid>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.25}>
          <VerdictLine label="Vendor differs" verdict={verdictText(discrepancy.vendorDiscrepant)} />
          <VerdictLine
            label="Document differs"
            verdict={verdictText(discrepancy.documentDiscrepant)}
          />
        </Stack>

        {discrepancy.vendorAlignmentSuspect === true && (
          <Alert severity="warning" sx={{ py: 0 }}>
            The vendor and e-Manifest copies list a different number of waste lines, so line{" "}
            {discrepancy.wasteLineNumber ?? "numbers"} does not name the same waste on both sides.
            The values above are a comparison of two different wastes, and Correct Vendor Data is
            unavailable until the lines are matched. Open the Waste Line Count finding on this
            manifest to match them.
          </Alert>
        )}
      </Box>
    </DiscrepancyRowExpanderShell>
  );
}
